import { useState } from 'react';
import { checkSlideQC } from '../api/deepzoomAPI';
import { getPostData } from '../api/postAPI';
import { saveSlideQCResult } from '../api/slideAPI';

export type QCItemStatus = 'pending' | 'checking' | 'valid' | 'invalid' | 'error' | 'skipped';

const HEAVY_EXTENSIONS = ['mrxs', 'ndpi', 'zip'];
const RETRY_DELAY_MS = 30 * 1000;

export interface SlideQCItem {
  slideId: number;
  filename: string;          // 서버에 저장된 파일명
  originalFilename: string;  // 사용자가 업로드한 원본 파일명
  status: QCItemStatus;
  checkedTiles?: number;
  corruptedCount?: number;
  errorMsg?: string;
}

const MAX_WAIT_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;

export const useSlideQC = () => {
  const [showQCModal, setShowQCModal] = useState(false);
  const [qcItems, setQcItems] = useState<SlideQCItem[]>([]);
  const [qcDone, setQcDone] = useState(false);
  const [qcHasIssue, setQcHasIssue] = useState(false);

  /**
   * 모든 업로드가 완료된 후 호출
   * @param postId          업로드 대상 포스트 ID
   * @param prevSlideIds    업로드 전 이미 존재하던 슬라이드 ID 집합
   * @param originalFilenames 사용자가 업로드한 원본 파일명 목록 (순서 중요)
   */
  const runQC = async (postId: number, prevSlideIds: Set<number>, originalFilenames: string[], onComplete?: () => void | Promise<void>) => {
    if (originalFilenames.length === 0) return;

    setQcDone(false);
    setQcHasIssue(false);
    const items: SlideQCItem[] = originalFilenames.map(name => ({
      slideId: 0,
      filename: '',
      originalFilename: name,
      status: 'pending',
    }));
    setQcItems([...items]);
    setShowQCModal(true);

    const flush = () => setQcItems([...items]);
    const qcTasks: Promise<void>[] = [];
    let hasIssue = false;
    let matchIndex = 0;

    const runSingleQC = async (slide: any, idx: number, originalFilename: string): Promise<void> => {
      items[idx] = { ...items[idx], slideId: slide.slideId, filename: slide.filename, status: 'checking' };
      flush();

      // 대용량 확장자는 QC 스킵
      const ext = originalFilename.split('.').pop()?.toLowerCase() ?? '';
      if (HEAVY_EXTENSIONS.includes(ext)) {
        items[idx] = { ...items[idx], status: 'skipped', errorMsg: '대용량 파일 — QC 미실시' };
        flush();
        saveSlideQCResult(slide.slideId, 'SKIPPED');
        return;
      }

      const tryCheck = () => checkSlideQC(postId, slide.filename);

      try {
        let result: Awaited<ReturnType<typeof tryCheck>>;
        try {
          result = await tryCheck();
        } catch (e: any) {
          if (e?.response?.status === 504) {
            // 504 → 30초 대기 후 1회 재시도
            items[idx] = { ...items[idx], errorMsg: '서버 응답 지연 — 재시도 중...' };
            flush();
            await new Promise<void>(r => setTimeout(r, RETRY_DELAY_MS));
            result = await tryCheck(); // 재시도 실패 시 catch로 빠짐
          } else {
            throw e;
          }
        }

        const corruptedCount = result.corrupted_tiles.length;
        const isValid = result.valid || corruptedCount === 0;
        items[idx] = {
          ...items[idx],
          status: isValid ? 'valid' : 'invalid',
          checkedTiles: result.checked,
          corruptedCount,
          errorMsg: result.error,
        };
        saveSlideQCResult(slide.slideId, isValid ? 'PASS' : 'FAIL');
        if (!isValid) { hasIssue = true; }
      } catch (e: any) {
        const status = e?.response?.status;
        const msg = status === 404 ? '딥줌 서버에 파일 없음' : '검사 요청 실패';
        items[idx] = { ...items[idx], status: 'error', errorMsg: msg };
        hasIssue = true;
        saveSlideQCResult(slide.slideId, 'ERROR');
      }

      flush();
    };

    // 새 슬라이드가 서버에 등록될 때마다 즉시 QC 시작 (병렬)
    const knownNewIds = new Set<number>();
    const expectedCount = originalFilenames.length;
    const startTime = Date.now();

    while (Date.now() - startTime < MAX_WAIT_MS) {
      const postData = await getPostData(postId);
      const allSlides: any[] = postData?.slides || [];
      const freshSlides = allSlides
        .filter((s: any) => !prevSlideIds.has(s.slideId) && !knownNewIds.has(s.slideId))
        .sort((a: any, b: any) => a.slideId - b.slideId); // FIFO 순서 유지

      for (const slide of freshSlides) {
        knownNewIds.add(slide.slideId);
        // 업로드 순서대로 원본 파일명에 매핑
        const idx = matchIndex < items.length ? matchIndex++ : (() => {
          const newIdx = items.length;
          items.push({ slideId: 0, filename: slide.filename, originalFilename: '(추가 슬라이드)', status: 'pending' });
          flush();
          return newIdx;
        })();
        const origName = items[idx]?.originalFilename ?? '';
        qcTasks.push(runSingleQC(slide, idx, origName));
      }

      if (knownNewIds.size >= expectedCount) break;
      await new Promise<void>(r => setTimeout(r, POLL_INTERVAL_MS));
    }

    if (knownNewIds.size === 0) {
      for (let i = 0; i < items.length; i++) {
        items[i] = { ...items[i], status: 'error', errorMsg: '서버 처리 시간 초과 또는 슬라이드 생성 실패' };
      }
      flush();
      setQcHasIssue(true);
      setQcDone(true);
      return;
    }

    await Promise.all(qcTasks);

    setQcHasIssue(hasIssue);
    setQcDone(true);
    if (!hasIssue) {
      setTimeout(() => setShowQCModal(false), 1500);
    }
    if (onComplete) await onComplete();
  };

  const closeQCModal = () => setShowQCModal(false);

  return { showQCModal, qcItems, qcDone, qcHasIssue, runQC, closeQCModal };
};
